import React, { useEffect, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { Badge, Button, Card, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

import JumboSearch from "@jumbo/components/JumboSearch";
import Div from "@jumbo/shared/Div/Div";
import {
  getCustomer,
  getDSOKCategories,
  setBackendDomain,
} from "app/services/api/management";

import jwtAuthAxiosKiosk from "app/services/auth/jwtAuthKiosk";
import { prepareCategoryLevels } from "app/utils/arrayHelpers";
import CategoryItem from "./CategoryItem";

const DSOKMenuCategories = ({ setFieldValue, values, setCloseModal }) => {
  const { getAuthUser } = useJumboAuth();
  const {
    customer: { url },
  } = getAuthUser();

  const { t, i18n } = useTranslation();
  const [searchName, setSearchName] = useState();

  const [categories, setCategories] = useState([]);
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const customerUrl = url || "";

  const loadCategories = async () => {
    await getDSOKCategories().then((categories) => {
      // const { categories } = response || {}
      const categoryArr = Object.keys(categories || {}).map((id) => ({
        ...categories[id],
      }));
      const leveledCategories = prepareCategoryLevels(categoryArr || []);

      const filteredCategories = searchName
        ? leveledCategories.filter(
            (category) =>
              category?.name &&
              category.name[defaultLanguage].toLowerCase().includes(searchName)
          )
        : leveledCategories;

      setCategories(filteredCategories || []);
      setDefaultCategories(leveledCategories || []);
    });
    setLoading(false);
  };

  const searchByName = async (searchName) => {
    setLoading(true);
    setBackendDomain(jwtAuthAxiosKiosk);
    if (!jwtAuthAxiosKiosk.defaults.baseURL) {
      await loadCustomer();
    }
    searchName !== undefined && loadCategories();
  };

  useEffect(() => {
    searchByName(searchName);
  }, [searchName]);

  const loadCustomer = async () => {
    await getCustomer(customerUrl).then((customer) => setCustomer(customer));
  };

  useEffect(() => {
    loadCustomer();
  }, []);

  const addToMenu = (category) => {
    if (setFieldValue && values) {
      const currentCategories = values.categories || {};
      setFieldValue("categories", {
        ...currentCategories,
        [category._id]: category,
      });
    }
    if (setCloseModal) {
      setCloseModal();
    }
  };

  const toggleSelect = (categoryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCategories.map((c) => c._id)));
    }
  };

  const addSelectedToMenu = () => {
    if (!setFieldValue || !values || selectedIds.size === 0) return;
    const currentCategories = values.categories || {};
    const additions = {};
    categories.forEach((c) => {
      if (selectedIds.has(c._id)) {
        additions[c._id] = c;
      }
    });
    setFieldValue("categories", { ...currentCategories, ...additions });
    if (setCloseModal) {
      setCloseModal();
    }
  };

  const alreadyInMenuIds = new Set(Object.keys(values?.categories || {}));
  const selectableCategories = categories.filter((c) => !alreadyInMenuIds.has(c._id));
  const allSelected = selectableCategories.length > 0 && selectedIds.size === selectableCategories.length;

  return (
    <React.Fragment>
      <Div
        sx={{
          position: "sticky",
          top: -32,
          zIndex: 10,
          bgcolor: "background.paper",
          pt: 1,
          pb: 1,
          mx: -4,
          px: 4,
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 2,
        }}
      >
        <Typography variant={"h2"} mb={2}>
          {t("pages.dsokCategories.title")}
        </Typography>
        <Stack
          direction="row"
          display="flex"
          alignItems="center"
          spacing={1}
        >
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="DSOKCategories"
            onChange={setSearchName}
            sx={{ width: "100%" }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        {categories?.length > 0 && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={toggleSelectAll}
            >
              {allSelected ? t("buttons.deselectAll") : t("buttons.selectAll")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={selectedIds.size === 0}
              startIcon={<PlaylistAddIcon />}
              onClick={addSelectedToMenu}
            >
              {t("buttons.addSelected")} ({selectedIds.size})
            </Button>
          </>
        )}
        </Stack>
      </Div>
      {categories?.length ? (
        categories.map((category, index) => (
          <CategoryItem
            key={index}
            item={category}
            disabled={loading}
            onAddToMenu={() => addToMenu(category)}
            productLanguages={languages}
            defaultLanguage={defaultLanguage}
            categories={defaultCategories}
            searchName={searchName}
            selected={selectedIds.has(category._id)}
            onToggleSelect={() => toggleSelect(category._id)}
            alreadyInMenu={alreadyInMenuIds.has(category._id)}
          />
        ))
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DSOKMenuCategories;
